import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  DEFAULT_COOKIE_PREFERENCES,
  OPEN_COOKIE_SETTINGS_EVENT,
  getCookiePreferences,
  saveCookiePreferences,
} from '../utils/cookieConsent';

const CookieConsent = () => {
  const location = useLocation();
  const [preferences, setPreferences] = useState(DEFAULT_COOKIE_PREFERENCES);
  const [ready, setReady] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    setPreferences(getCookiePreferences());
    setReady(true);
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => setShowSettings(true);
    const handlePreferenceChange = (event) => {
      setPreferences(event.detail || getCookiePreferences());
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    window.addEventListener('swapify:cookie-preferences-changed', handlePreferenceChange);

    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
      window.removeEventListener('swapify:cookie-preferences-changed', handlePreferenceChange);
    };
  }, []);

  if (!ready) {
    return null;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');
  if (isAdminRoute) {
    return null;
  }

  const showBanner = !preferences.consentGiven;

  const acceptAll = () => {
    const next = saveCookiePreferences({
      necessary: true,
      analytics: true,
    });
    setPreferences(next);
    setShowSettings(false);
  };

  const acceptNecessaryOnly = () => {
    const next = saveCookiePreferences({
      necessary: true,
      analytics: false,
    });
    setPreferences(next);
    setShowSettings(false);
  };

  const saveSettings = () => {
    const next = saveCookiePreferences(preferences);
    setPreferences(next);
    setShowSettings(false);
  };

  return (
    <>
      {showBanner ? (
        <div className="fixed bottom-0 inset-x-0 z-50 px-4 pb-4">
          <div className="mx-auto max-w-5xl rounded-3xl border border-gray-200 bg-white/95 shadow-2xl backdrop-blur-md">
            <div className="flex flex-col gap-5 px-6 py-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-500">Cookies</p>
                <h2 className="mt-2 text-xl font-bold text-gray-900">Kies hoe Swapify cookies gebruikt</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  We gebruiken noodzakelijke cookies voor inloggen, winkelwagen en beveiliging. Analytische cookies
                  staan standaard uit en worden pas later gebruikt als jij daar toestemming voor geeft.
                  Lees meer in ons <Link to="/privacy" className="font-semibold text-red-500 hover:text-red-600">privacy- en cookiebeleid</Link>.
                </p>
              </div>
              <div className="flex flex-col gap-3 md:min-w-[260px]">
                <button
                  onClick={acceptAll}
                  className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:shadow-lg"
                >
                  Accepteer
                </button>
                <button
                  onClick={acceptNecessaryOnly}
                  className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:border-gray-400"
                >
                  Alleen noodzakelijk
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="rounded-xl px-5 py-2 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
                >
                  Instellingen
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showSettings ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-500">Cookie-instellingen</p>
                <h2 className="mt-2 text-2xl font-bold text-gray-900">Beheer je voorkeuren</h2>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-2xl leading-none text-gray-400 transition hover:text-gray-600"
                aria-label="Sluiten"
              >
                ×
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">Noodzakelijke cookies</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Nodig voor inloggen, beveiliging, sessies en je winkelwagen. Deze staan altijd aan.
                    </p>
                  </div>
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">Altijd aan</span>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">Analytische cookies</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Voor toekomstige inzichten in gebruik en prestaties. In deze fase worden nog geen analytics-scripts geladen.
                    </p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(event) => setPreferences((current) => ({
                        ...current,
                        analytics: event.target.checked,
                      }))}
                      className="h-5 w-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={acceptNecessaryOnly}
                className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:border-gray-400"
              >
                Alleen noodzakelijk
              </button>
              <button
                onClick={saveSettings}
                className="rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:shadow-lg"
              >
                Voorkeuren opslaan
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CookieConsent;
